import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format, parse } from "date-fns";
import { Sparkles, Loader2, ChevronRight, ChevronLeft, Instagram, Linkedin, Twitter, Facebook, Music2, Mic, TrendingUp, RefreshCcw, CalendarIcon, Clock } from "lucide-react";
import Sparkline from "../components/Sparkline";
import { VoiceOption } from "../components/VoiceOption";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "twitter", label: "X / Twitter", icon: Twitter },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "tiktok", label: "TikTok", icon: Music2 },
];
const PILLARS = [
  { key: "educational", label: "Educational", hint: "Tips, how-tos, teaching" },
  { key: "promotional", label: "Promotional", hint: "Products, offers, launches" },
  { key: "engagement", label: "Engagement", hint: "Questions, polls, community" },
  { key: "news", label: "Industry News", hint: "Trends & commentary" },
];

export default function BatchGenerator() {
  const navigate = useNavigate();
  const { selected, brands } = useBrand();
  const activeBrand = brands.find((b) => b.name === selected);
  const brandPillars = activeBrand?.pillars?.length ? activeBrand.pillars : null;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const [subBrands, setSubBrands] = useState([]);
  const [brandMix, setBrandMix] = useState(null);
  const [form, setForm] = useState({
    name: "", start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    duration_days: 14, frequency: "3x_week", post_time: "10:00", use_best_times: false,
    platforms: ["instagram", "linkedin"],
    pillars: { educational: 40, promotional: 30, engagement: 20, news: 10 },
    video_count: 0, video_voice: "british", video_engine: "slideshow",
    add_duet: false, duet_topic: "",
  });

  useEffect(() => {
    api.get("/brands").then((r) => {
      setSubBrands(r.data);
      if (r.data.length) {
        const even = Math.floor(100 / r.data.length);
        const mix = {};
        r.data.forEach((b, i) => { mix[b.name] = i === 0 ? 100 - even * (r.data.length - 1) : even; });
        setBrandMix(mix);
      }
    });
  }, []);

  useEffect(() => {
    if (brandPillars) {
      const p = {};
      brandPillars.forEach((x) => { p[x.name] = x.ratio; });
      setForm((f) => ({ ...f, pillars: p, post_time: activeBrand.posting_time || f.post_time }));
    } else {
      setForm((f) => ({ ...f, pillars: { educational: 40, promotional: 30, engagement: 20, news: 10 } }));
    }
  }, [selected, brands.length]);

  const loadBatches = () => api.get("/batches", { params: selected ? { brand: selected } : {} }).then((r) => setBatches(r.data));
  const [bestTime, setBestTime] = useState(null);
  useEffect(() => { api.get("/best-times", { params: { brand: selected } }).then((r) => setBestTime(r.data?.recommended_time ? r.data : null)); }, [selected]);
  useEffect(() => { loadBatches(); const t = setInterval(loadBatches, 6000); return () => clearInterval(t); }, [selected]);

  const [boost, setBoost] = useState(null);
  const [boostApplied, setBoostApplied] = useState(false);
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    setBoostApplied(false);
    const params = selected ? { brand: selected } : {};
    api.get("/analytics/next-batch-boost", { params }).then((r) => setBoost(r.data)).catch(() => setBoost(null));
    api.get("/analytics/pillar-trend", { params: { ...params, weeks: 6 } }).then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, [selected]);

  const applyBoost = () => {
    if (!boost?.suggested_boost) return;
    const winnerKey = boost.suggested_boost.pillar;
    const delta = boost.suggested_boost.delta_pct || 15;
    setForm((f) => {
      const current = { ...f.pillars };
      const keys = Object.keys(current);
      if (!keys.includes(winnerKey)) return f;
      const winnerNew = Math.min(85, (current[winnerKey] || 0) + delta);
      const consumed = winnerNew - (current[winnerKey] || 0);
      const others = keys.filter((k) => k !== winnerKey);
      const othersTotal = others.reduce((s, k) => s + (current[k] || 0), 0) || 1;
      others.forEach((k) => {
        const share = (current[k] || 0) / othersTotal;
        current[k] = Math.max(0, Math.round((current[k] || 0) - consumed * share));
      });
      current[winnerKey] = winnerNew;
      const voiceKey = boost.suggested_boost.video_voice;
      const nextForm = { ...f, pillars: current };
      if (voiceKey && ["warm","energetic","british","adriana","alberto","aria","amelia"].includes(voiceKey)) {
        nextForm.video_voice = voiceKey;
      }
      return nextForm;
    });
    setBoostApplied(true);
    toast.success(`Boosted "${winnerKey}" by +${delta}pt${boost.suggested_boost.video_voice ? ` · voice → ${boost.suggested_boost.video_voice}` : ""}`);
  };

  const togglePlatform = (id) => setForm((f) => ({ ...f, platforms: f.platforms.includes(id) ? f.platforms.filter((p) => p !== id) : [...f.platforms, id] }));
  const setPillar = (key, val) => setForm((f) => ({ ...f, pillars: { ...f.pillars, [key]: val } }));
  const pillarTotal = Object.values(form.pillars).reduce((a, b) => a + b, 0);

  const launch = async () => {
    if (!form.name.trim()) return toast.error("Give your campaign a name");
    if (!form.platforms.length) return toast.error("Pick at least one platform");
    if (activeBrand?.status === "draft") return toast.error(`${activeBrand.name} is a draft brand — activate it in Admin → Identity & safety first`);
    setBusy(true);
    try {
      const { add_duet, duet_topic, ...batchPayload } = form;
      const { data: batch } = await api.post("/batches", { ...batchPayload, start_date: `${form.start_date}T00:00:00`, brand: selected || null, brands: selected ? null : brandMix, days: activeBrand?.posting_days || null });
      toast.success("Batch generation started — posts will appear in Review Feed shortly");
      if (add_duet && duet_topic.trim() && selected === "Roujaune" && batch?.id) {
        // Fire and forget — duet render can take a few minutes and shouldn't block the launch flow.
        api.post(`/batches/${batch.id}/duet-post`, { topic: duet_topic.trim(), platform: form.platforms[0] || "facebook" })
          .then(() => toast.success(`Adriana × Alberto duet queued on "${duet_topic}" — dialogue video rendering in the background`))
          .catch((e) => toast.error(`Duet queued failed: ${errText(e)}`));
      }
      setStep(0);
      loadBatches();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const est = () => {
    const perWeek = { daily: 7, "5x_week": 5, "3x_week": 3 }[form.frequency];
    return Math.min(30, Math.round((form.duration_days / 7) * perWeek));
  };

  return (
    <div className="space-y-8 fade-up" data-testid="generator-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">AI Post Generator{selected ? ` · ${selected}` : ""}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{selected ? `Content for ${selected}.` : "Plan weeks of content in one run."}</h1>
        {selected && <p className="text-muted-foreground mt-1">Every post in this batch is written for {selected} and its own social accounts.</p>}
      </div>

      <Card className="max-w-3xl">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            {[0, 1, 2].map((i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-accent" : "bg-muted"}`} />)}
          </div>
          <CardTitle className="font-heading text-xl">{["Campaign basics", "Platforms & cadence", "Content pillar mix"][step]}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 0 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Campaign name</Label>
                <Input data-testid="campaign-name-input" placeholder="November product push" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="start-date-input"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon size={14} className="mr-2 text-muted-foreground" />
                        {form.start_date
                          ? format(parse(form.start_date, "yyyy-MM-dd", new Date()), "EEE, MMM d")
                          : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0" data-testid="start-date-popover">
                      <Calendar
                        mode="single"
                        selected={form.start_date ? parse(form.start_date, "yyyy-MM-dd", new Date()) : undefined}
                        onSelect={(d) => d && setForm({ ...form, start_date: format(d, "yyyy-MM-dd") })}
                        disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <div className="flex gap-2">
                    {[14, 21, 30].map((d) => (
                      <Button key={d} type="button" data-testid={`duration-${d}-btn`} variant={form.duration_days === d ? "default" : "outline"} size="sm" onClick={() => setForm({ ...form, duration_days: d })}>{d}d</Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Post time</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="post-time-input"
                        disabled={form.use_best_times}
                        className="w-full justify-start text-left font-normal"
                      >
                        <Clock size={14} className="mr-2 text-muted-foreground" />
                        {form.post_time || "Pick a time"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-3" data-testid="post-time-popover">
                      <div className="flex gap-2 max-h-56">
                        <div className="flex flex-col gap-1 overflow-y-auto pr-2 border-r border-border w-14">
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold sticky top-0 bg-popover pb-1">Hour</p>
                          {Array.from({ length: 24 }, (_, h) => {
                            const label = String(h).padStart(2, "0");
                            const active = form.post_time.split(":")[0] === label;
                            return (
                              <button
                                key={h}
                                type="button"
                                data-testid={`post-time-hour-${label}`}
                                onClick={() => {
                                  const [, mm = "00"] = form.post_time.split(":");
                                  setForm({ ...form, post_time: `${label}:${mm}` });
                                }}
                                className={`text-sm py-1 px-2 rounded ${active ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                              >{label}</button>
                            );
                          })}
                        </div>
                        <div className="flex flex-col gap-1 overflow-y-auto w-14">
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold sticky top-0 bg-popover pb-1">Min</p>
                          {["00", "15", "30", "45"].map((mm) => {
                            const active = (form.post_time.split(":")[1] || "00") === mm;
                            return (
                              <button
                                key={mm}
                                type="button"
                                data-testid={`post-time-min-${mm}`}
                                onClick={() => {
                                  const [hh = "10"] = form.post_time.split(":");
                                  setForm({ ...form, post_time: `${hh}:${mm}` });
                                }}
                                className={`text-sm py-1 px-2 rounded ${active ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                              >{mm}</button>
                            );
                          })}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <label className="mt-1.5 flex items-start gap-2 cursor-pointer" data-testid="auto-best-time-toggle">
                    <input type="checkbox" className="mt-0.5 accent-current" checked={form.use_best_times} onChange={(e) => setForm({ ...form, use_best_times: e.target.checked })} />
                    <span className="text-xs text-accent flex items-center gap-1"><Sparkles size={11} /> Auto best timing — schedule each post at its platform's best engagement time</span>
                  </label>
                  {!form.use_best_times && bestTime?.recommended_time && (
                    <button type="button" data-testid="use-best-time-btn" onClick={() => { setForm({ ...form, post_time: bestTime.recommended_time }); toast.success(`Using AI best time ${bestTime.recommended_time}`); }}
                      className="mt-1.5 text-xs text-accent flex items-center gap-1.5 hover:underline text-left">
                      <Sparkles size={11} /> AI best time: {bestTime.recommended_time} ({bestTime.slots?.[0]?.day_name}) · learned {bestTime.learned_at ? new Date(bestTime.learned_at).toLocaleDateString() : ""} — tap to use
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">Target platforms</Label>
                <div className="flex flex-wrap gap-3">
                  {PLATFORMS.map((p) => (
                    <button key={p.id} data-testid={`platform-${p.id}-btn`} onClick={() => togglePlatform(p.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-colors ${form.platforms.includes(p.id) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-foreground"}`}>
                      <p.icon size={15} /> {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-3 block">Posting frequency</Label>
                <div className="flex gap-3">
                  {[{ v: "3x_week", l: "3× / week" }, { v: "5x_week", l: "5× / week" }, { v: "daily", l: "Daily" }].map((f) => (
                    <Button key={f.v} type="button" data-testid={`freq-${f.v}-btn`} variant={form.frequency === f.v ? "default" : "outline"} onClick={() => setForm({ ...form, frequency: f.v })}>{f.l}</Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1 block">Video posts <span className="text-muted-foreground font-normal text-xs">AI slideshow + voiceover, published as Reels</span></Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {[0, 1, 2, 3, 5].map((n) => (
                    <Button key={n} type="button" size="sm" data-testid={`video-count-${n}-btn`} variant={form.video_count === n ? "default" : "outline"} onClick={() => setForm({ ...form, video_count: n })}>{n === 0 ? "None" : `${n} video${n > 1 ? "s" : ""}`}</Button>
                  ))}
                </div>
                {form.video_count > 0 && (
                  <>
                    <div className="flex gap-2 mt-3">
                      {[{ v: "slideshow", l: "Slideshow (free)" }, { v: "veo", l: "Motion AI · Veo (~$1/scene)" }].map((o) => (
                        <Button key={o.v} type="button" size="sm" data-testid={`video-engine-${o.v}-btn`} variant={form.video_engine === o.v ? "default" : "outline"} onClick={() => setForm({ ...form, video_engine: o.v })}>{o.l}</Button>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {[{ v: "british", l: "British female voice" }, { v: "warm", l: "Warm female voice" }, { v: "energetic", l: "Energetic male voice" }, { v: "adriana", l: "Adriana · Roujaune coach" }, { v: "alberto", l: "Alberto · Roujaune coach" }, { v: "aria", l: "Aria · FB50 companion coach" }, { v: "amelia", l: "Amelia · Peaceful Pause companion" }].map((o) => (
                        <VoiceOption key={o.v} voiceKey={o.v} label={o.l}
                                     selected={form.video_voice === o.v}
                                     onSelect={() => setForm({ ...form, video_voice: o.v })} />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {selected === "Roujaune" && (
                <div className="rounded-lg border border-dashed border-accent/60 bg-accent/5 p-4 space-y-2" data-testid="duet-batch-section">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" className="mt-0.5 accent-current" data-testid="duet-batch-toggle"
                      checked={form.add_duet} onChange={(e) => setForm({ ...form, add_duet: e.target.checked })} />
                    <span className="text-sm">
                      <span className="font-semibold">Also add a Coach Duet post</span>
                      <span className="block text-xs text-muted-foreground">Adriana × Alberto trade tips on one topic in a 45–60 s Veo dialogue video. Rendered once at the end of this batch.</span>
                    </span>
                  </label>
                  {form.add_duet && (
                    <input type="text" data-testid="duet-batch-topic"
                      value={form.duet_topic}
                      onChange={(e) => setForm({ ...form, duet_topic: e.target.value })}
                      placeholder="Duet topic e.g. Race day fuelling"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground">≈ <span className="font-semibold text-foreground">{est()} posts</span> will be generated{form.video_count > 0 ? `, ${form.video_count} of them as narrated videos` : ""}.</p>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              {boost?.top_pillar && boost.posts_analysed > 0 && (
                <div className="rounded-lg border border-accent/40 bg-accent/5 p-4" data-testid="winners-boost-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-accent">
                        <TrendingUp size={14} /> Last {Math.ceil((new Date(boost.window_end) - new Date(boost.window_start)) / (7*86400000))}-week winners
                        {!boost.has_engagement_data && <Badge variant="outline" className="text-[10px]">by volume</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Across {boost.posts_analysed} posts {selected ? `for ${selected}` : "across all brands"} · {boost.window_start} → {boost.window_end}.</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-md bg-background border px-2 py-1 inline-flex items-center gap-2">
                          <b>Top pillar:</b>
                          <span className="capitalize" data-testid="winners-top-pillar">{boost.top_pillar.name}</span>
                          {boost.top_pillar.score > 0 ? ` · score ${boost.top_pillar.score}` : ` · ${boost.top_pillar.posts} posts`}
                          {trend?.series && (
                            <span className="text-accent inline-flex items-center gap-1 ml-1" title="6-week trend">
                              <Sparkline
                                data-testid="winners-sparkline"
                                values={trend.series.map((w) => (w.pillars?.[boost.top_pillar.name]?.score || w.pillars?.[boost.top_pillar.name]?.posts || 0))}
                                width={70} height={20} stroke="var(--accent, #c69d56)" fill="var(--accent, #c69d56)" />
                            </span>
                          )}
                        </span>
                        {boost.top_companion && (
                          <span className="rounded-md bg-background border px-2 py-1"><b>Top companion:</b> <span data-testid="winners-top-companion">{boost.top_companion.display_name || boost.top_companion.name}</span>{boost.top_companion.score > 0 ? ` · score ${boost.top_companion.score}` : ` · ${boost.top_companion.posts} posts`}</span>
                        )}
                      </div>
                      {boost.rotation_target && (
                        <div className="mt-3 rounded-md border border-accent/30 bg-background p-2 text-xs" data-testid="winners-rotation-note">
                          <div className="font-semibold text-accent flex items-center gap-1"><RefreshCcw size={11} /> Rotation guard on</div>
                          <div className="text-muted-foreground mt-0.5">
                            <b>{boost.rotation_target.name}</b> has been at {boost.rotation_target.share_pct}% (below the {boost.companion_floor_pct}% floor) — the boost will use their voice this batch to keep the roster balanced.
                          </div>
                        </div>
                      )}
                      {boost.all_companion_share?.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]" data-testid="winners-companion-shares">
                          {boost.all_companion_share.map((c) => (
                            <div key={c.key} className={`rounded-md border p-1.5 ${c.below_floor ? "border-destructive/40 bg-destructive/5" : "bg-background"}`}>
                              <div className="font-semibold truncate">{c.name}</div>
                              <div className={`${c.below_floor ? "text-destructive" : "text-muted-foreground"}`}>{c.share_pct}% · {c.posts} posts</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button size="sm" onClick={applyBoost} disabled={boostApplied}
                            data-testid="apply-boost-btn" variant={boostApplied ? "outline" : "default"}>
                      {boostApplied
                        ? <>✓ Boost applied</>
                        : <>Boost "{boost.top_pillar.name}" +{boost.suggested_boost?.delta_pct || 15}pt{boost.suggested_boost?.video_voice ? ` · voice → ${boost.suggested_boost.video_voice}` : ""}</>}
                    </Button>
                  </div>
                </div>
              )}
              {(brandPillars || PILLARS.map((p) => ({ name: p.key, label: p.label, description: p.hint }))).map((p) => {
                const key = brandPillars ? p.name : p.name;
                const label = brandPillars ? p.name : PILLARS.find((x) => x.key === p.name)?.label;
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <Label>{label} <span className="text-muted-foreground font-normal text-xs ml-1">{p.description}</span></Label>
                      <span className="font-heading font-semibold text-accent">{form.pillars[key] ?? 0}%</span>
                    </div>
                    <Slider data-testid={`pillar-${key}-slider`} value={[form.pillars[key] ?? 0]} max={100} step={5} onValueChange={([v]) => setPillar(key, v)} />
                  </div>
                );
              })}
              <p className={`text-sm font-medium ${pillarTotal === 100 ? "text-muted-foreground" : "text-destructive"}`}>Total: {pillarTotal}% {pillarTotal !== 100 && "(will be normalized)"}</p>
              {brandPillars && <p className="text-xs text-muted-foreground">Using {selected}'s configured pillar mix — edit defaults in Admin → Identity & safety.</p>}

              <div className="pt-4 border-t border-border" data-testid="pillar-heatmap">
                <Label className="text-base">Next 7 days at a glance</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-3">Simulated pillar mix for the first week — spot over-posting before you generate.</p>
                <div className="grid grid-cols-7 gap-1.5">
                  {(() => {
                    const perWeek = { daily: 7, "5x_week": 5, "3x_week": 3 }[form.frequency];
                    const pillarEntries = Object.entries(form.pillars).filter(([, v]) => v > 0);
                    const total = pillarEntries.reduce((s, [, v]) => s + v, 0) || 1;
                    // Deterministic Bresenham-like distribution across 7 days
                    const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
                    const active = new Array(7).fill(null);
                    const pattern = { daily:[0,1,2,3,4,5,6], "5x_week":[0,1,2,3,4], "3x_week":[0,2,4] }[form.frequency];
                    const slots = [];
                    pillarEntries.forEach(([k, v]) => {
                      const n = Math.max(1, Math.round((v / total) * pattern.length));
                      for (let i = 0; i < n; i++) slots.push(k);
                    });
                    while (slots.length < pattern.length) slots.push(pillarEntries[0]?.[0]);
                    slots.length = pattern.length;
                    pattern.forEach((d, i) => { active[d] = slots[i]; });
                    const pillarLabel = (k) => brandPillars ? k : (PILLARS.find((p) => p.key === k)?.label || k);
                    const swatch = { educational:"#7aa2ff", promotional:"#ff8e6b", engagement:"#7dd3a4", news:"#c68bd6" };
                    const colorFor = (k) => swatch[k] || `hsl(${(k.charCodeAt(0)*47) % 360},60%,60%)`;
                    return days.map((d, i) => (
                      <div key={d} className="rounded-md border border-border p-1.5 text-center min-h-[56px] flex flex-col justify-between" title={active[i] ? pillarLabel(active[i]) : "Rest day"}>
                        <span className="text-[10px] text-muted-foreground">{d}</span>
                        {active[i] ? (
                          <div className="rounded-sm w-full h-6" style={{ background: colorFor(active[i]), boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.15)" }} />
                        ) : (
                          <div className="w-full h-6 rounded-sm bg-muted opacity-40" />
                        )}
                        <span className="text-[9px] font-medium truncate">{active[i] ? pillarLabel(active[i]).split(" ")[0] : "—"}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              {!selected && brandMix && subBrands.length > 0 && (
                <div className="pt-4 border-t border-border space-y-5" data-testid="brand-mix-section">
                  <div>
                    <Label className="text-base">Brand mix</Label>
                    <p className="text-xs text-muted-foreground mt-1">Balance this batch across your brands — each post is written in that brand's voice. Manage brands in Brand Knowledge.</p>
                  </div>
                  {subBrands.map((b) => (
                    <div key={b.id} className="space-y-2">
                      <div className="flex justify-between items-baseline">
                        <Label>{b.name}</Label>
                        <span className="font-heading font-semibold text-accent">{brandMix[b.name] ?? 0}%</span>
                      </div>
                      <Slider data-testid={`brand-mix-${b.id}-slider`} value={[brandMix[b.name] ?? 0]} max={100} step={5} onValueChange={([v]) => setBrandMix({ ...brandMix, [b.name]: v })} />
                    </div>
                  ))}
                  <p className={`text-sm font-medium ${Object.values(brandMix).reduce((a, c) => a + c, 0) === 100 ? "text-muted-foreground" : "text-destructive"}`}>
                    Brand total: {Object.values(brandMix).reduce((a, c) => a + c, 0)}% {Object.values(brandMix).reduce((a, c) => a + c, 0) !== 100 && "(will be normalized)"}
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between pt-2">
            <Button variant="ghost" data-testid="wizard-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)}><ChevronLeft size={15} className="mr-1" /> Back</Button>
            {step < 2 ? (
              <Button data-testid="wizard-next-btn" onClick={() => setStep(step + 1)}>Next <ChevronRight size={15} className="ml-1" /></Button>
            ) : (
              <Button data-testid="launch-batch-btn" disabled={busy} onClick={launch} className="min-w-[180px]">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <><Sparkles size={15} className="mr-2" /> Generate {est()} posts</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="font-heading text-2xl font-semibold">Your batches</h2>
        {batches.length === 0 && <p className="text-sm text-muted-foreground">No batches yet — launch your first campaign above.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {batches.map((b) => (
            <Card key={b.id} data-testid={`batch-card-${b.id}`} className="hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 cursor-pointer" onClick={() => b.status === "ready" && navigate(`/review?batch=${b.id}`)}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">{b.name}</p>
                  <Badge variant={b.status === "ready" ? "default" : b.status === "failed" ? "destructive" : "secondary"} data-testid={`batch-status-${b.id}`}>
                    {b.status === "generating" ? <span className="flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> generating</span> : b.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{b.duration_days} days · {b.frequency.replace("_", " ")} · {b.platforms.join(", ")}</p>
                <p className="text-sm">{b.total_posts > 0 ? `${b.total_posts} posts` : "—"}{b.total_videos ? ` · ${b.videos_done ?? 0}/${b.total_videos} videos` : ""}{b.locked && " · 🔒 locked"}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
